"use client";
import dynamic from "next/dynamic";
import { Track } from "livekit-client";
import { VideoGrid } from "./VideoGrid";
import { PipPortal } from "./PipPortal";
import { PipContent } from "./PipContent";
import { Logger } from "@shared/utils/logger";
import { MediaControls } from "./MediaControls";
import { isWeb } from "@livekit/components-core";
import { RemoteParticipant } from "livekit-client";
import { MeetingEndedView } from "./MeetingEndedView";
import { ScreenShareBanner } from "./ScreenShareBanner";
import { VideoPipFallback } from "./VideoPipFallback";
import { DebugMode } from "@client/components/ui/Debug";
import { useAppSelector } from "@client/store/store.hooks";
import { HostLeaveOption } from "@shared/constants/constant";
import { usePictureInPicture } from "./hooks/usePictureInPicture";
import { useParticipantAvatarByIdentity } from "./hooks/useParticipantAvatarByIdentity";
import { MeetContentProps } from "@client/types/meetContent.types";
import { useTranslations } from "next-intl";
import { JSX, memo, useMemo, useRef, useState, useCallback } from "react";
import { KeyboardShortcuts } from "@client/components/ui/KeyboardShortcuts";
import { RecordingIndicator } from "@client/components/ui/RecordingIndicator";
import { useVideoPictureInPicture } from "@client/components/meet/hooks/useVideoPictureInPicture";
import { getBreakoutTheme, type BreakoutTheme } from "@client/utils/breakoutThemes";
import { BreakoutStatusBar, BreakoutAssignedPromptBar } from "@client/components/meet/BreakoutRoomAmbience";
import {
  useTracks,
  AudioTrack,
  useRoomContext,
  useParticipants,
  useLocalParticipant,
  LayoutContextProvider,
  useCreateLayoutContext,
  TrackReference,
  TrackReferenceOrPlaceholder
} from "@livekit/components-react";
// Dynamic imports for conditionally-rendered sidebar components (CRITICAL: bundle optimization)
const ChatIframeSidebar = dynamic(() => import("./ChatIframeSidebar").then((m) => m.ChatIframeSidebar), { ssr: false });
const ChatSidebar = dynamic(() => import("./ChatSidebar").then((m) => m.ChatSidebar), { ssr: false });
const SharedNotes = dynamic(() => import("./SharedNotes").then((m) => m.SharedNotes), { ssr: false });
const PollsSidebar = dynamic(() => import("./PollsSidebar").then((m) => m.PollsSidebar), { ssr: false });
const LeaveSidebar = dynamic(() => import("./LeaveSidebar").then((m) => m.LeaveSidebar), { ssr: false });
const SettingsSidebar = dynamic(() => import("./SettingsSidebar").then((m) => m.SettingsSidebar), { ssr: false });
const ParticipantsSidebar = dynamic(() => import("./ParticipantsSidebar").then((m) => m.ParticipantsSidebar), {
  ssr: false
});

// Less frequently used components
const Whiteboard = dynamic(() => import("./Whiteboard").then((m) => m.Whiteboard), { ssr: false });
const JoinRequest = dynamic(() => import("./JoinRequest").then((m) => m.JoinRequest), { ssr: false });
const MeetingDetails = dynamic(() => import("./MeetingDetails").then((m) => m.MeetingDetails), { ssr: false });
const ReactionOverlay = dynamic(() => import("./ReactionOverlay").then((m) => m.ReactionOverlay), { ssr: false });
const RecordingControls = dynamic(() => import("./RecordingControls").then((m) => m.RecordingControls), { ssr: false });

/** MeetContent - The main content component that renders the meeting UI */
const MeetContent = memo(function MeetContent(props: MeetContentProps): JSX.Element {
  const t = useTranslations();
  const _e2eeEnabled = useAppSelector((state) => state.meeting.meetingDetails?.e2eeEnabled ?? false);

  const participants = useParticipants();
  const localParticipant = useLocalParticipant();
  const layoutContext = useCreateLayoutContext();
  const _room = useRoomContext();
  const localAvatarUrl = props.connectionDetails.avatarUrl ?? null;
  const participantAvatarByIdentity = useParticipantAvatarByIdentity(localAvatarUrl);

  const breakoutTheme = useMemo((): BreakoutTheme | null => {
    if (!props.breakoutRoomId) return null;
    const name = props.breakoutRoomTitle || "Breakout";
    const color = props.breakoutThemeColor || getBreakoutTheme(props.breakoutRoomId).color;
    return { name, color };
  }, [props.breakoutRoomId, props.breakoutRoomTitle, props.breakoutThemeColor]);

  const assignedBreakoutPromptTheme = useMemo((): BreakoutTheme | null => {
    if (!props.pendingBreakoutAssignment) return null;
    return {
      name: props.pendingBreakoutAssignment.breakoutTitle,
      color:
        props.pendingBreakoutAssignment.breakoutThemeColor ||
        getBreakoutTheme(props.pendingBreakoutAssignment.breakoutRoomId).color
    };
  }, [props.pendingBreakoutAssignment]);

  const raisedHandsCount = useMemo(() => {
    let count = 0;
    props.participantReactions.forEach((reaction, participantId) => {
      if (reaction.isHandRaised && participantId !== localParticipant.localParticipant.sid) {
        count++;
      }
    });
    return count;
  }, [props.participantReactions, localParticipant.localParticipant.sid]);

  /**
   * Memoize participant list.
   * Filters out waiting room participants (canPublish=false).
   */
  const remoteParticipants = useMemo(
    () =>
      participants.filter((p) => {
        // Exclude local participant
        if (p.sid === localParticipant.localParticipant.sid) return false;
        // Exclude waiting room participants (lobby tokens have canPublish=false)
        if (p.permissions && p.permissions.canPublish === false) return false;
        return true;
      }) as RemoteParticipant[],
    [participants, localParticipant.localParticipant.sid]
  );

  // Memoize host identity
  const hostIdentity = useMemo(
    () => (props.isHost ? localParticipant.localParticipant.identity : props.connectionDetails.hostId.toString()),
    [props.isHost, localParticipant.localParticipant.identity, props.connectionDetails.hostId]
  );

  // Memoize local participant name
  const localParticipantName = localParticipant.localParticipant.name || "You";
  const localParticipantSid = localParticipant.localParticipant.sid;

  // Check if any sidebar is open
  const isSidebarOpen =
    props.showChat ||
    props.showParticipants ||
    props.showPolls ||
    props.showSettings ||
    props.showNotes ||
    props.showLeaveModal;

  // Determine active screen sharer (local or remote)
  const remoteScreenSharer = remoteParticipants.find((p) => p.isScreenShareEnabled);
  const isSomeoneElseSharing = !!remoteScreenSharer;

  // Ref for the video container to handle Fullscreen
  const videoContainerRef = useRef<HTMLDivElement>(null);

  // Focus the meeting tab (for PiP return button)
  const focusMeetingTab = useCallback(() => {
    window.focus();
  }, []);

  // Get all tracks to determine what to show in PiP
  const tracks = useTracks([Track.Source.Camera, Track.Source.ScreenShare]);
  // Screen-share audio is a separate LiveKit track and must be attached to an
  // audio element for viewers to hear tab/system audio.
  const screenShareAudioTracks = useTracks([{ source: Track.Source.ScreenShareAudio, withPlaceholder: false }], {
    onlySubscribed: true
  });

  // Determine active PiP tracks (Grid View: Top 4 priority)
  const pipTracks = useMemo(() => {
    // Priority:
    // 1. Screen Shares (Remote)
    // 2. Remote Participants (Camera ON or OFF) - EXCLUDING waiting room
    // 3. Local Camera/Placeholder

    const maxTiles = 4;
    const resultTracks: TrackReferenceOrPlaceholder[] = []; // using any temporarily to mix TrackReference and constructed placeholders

    // 1. Screen Shares (Remote + Local) - EXCLUDING waiting room screen shares
    const activeScreens = tracks.filter((t) => {
      if (t.source !== Track.Source.ScreenShare) return false;
      // Exclude waiting room participants
      if (t.participant.permissions && t.participant.permissions.canPublish === false) return false;
      return true;
    });
    resultTracks.push(...activeScreens);

    // 2. Remote Participants (Camera or Avatar) - Use already filtered remoteParticipants
    // remoteParticipants already excludes local and waiting room participants
    remoteParticipants.forEach((p) => {
      if (resultTracks.length >= maxTiles) return;

      // Try to find a camera track for this participant
      const camTrack = tracks.find((t) => t.participant.identity === p.identity && t.source === Track.Source.Camera);

      if (camTrack) {
        resultTracks.push(camTrack);
      } else {
        // Create placeholder
        resultTracks.push({
          participant: p,
          source: Track.Source.Camera,
          publication: undefined
        });
      }
    });

    // 3. Local Participant (if space allows) - Exclude if in waiting room
    if (resultTracks.length < maxTiles) {
      // Check if local participant is in waiting room
      const isLocalWaiting = localParticipant.localParticipant.permissions?.canPublish === false;

      if (!isLocalWaiting) {
        const localCam = tracks.find((t) => t.source === Track.Source.Camera && t.participant.isLocal);
        if (localCam) {
          resultTracks.push(localCam);
        } else {
          resultTracks.push({
            participant: localParticipant.localParticipant,
            source: Track.Source.Camera,
            publication: undefined
          });
        }
      }
    }

    return resultTracks.slice(0, maxTiles);
  }, [tracks, remoteParticipants, localParticipant.localParticipant]);

  // Primary track for video element PiP fallback (Firefox/Safari)
  // Priority: screen share > first remote participant > local
  const primaryPipTrack = useMemo(() => {
    if (pipTracks.length === 0) return null;
    // Screen share has highest priority
    const screenShare = pipTracks.find((t) => t.source === Track.Source.ScreenShare);
    if (screenShare) return screenShare;
    // Then first remote participant
    const remote = pipTracks.find((t) => !t.participant.isLocal);
    if (remote) return remote;
    // Fallback to local
    return pipTracks[0];
  }, [pipTracks]);

  // Check if local participant has waiting room permissions (canPublish = false)
  const isWaiting = localParticipant.localParticipant.permissions?.canPublish === false;

  // Automatic Picture-in-Picture on tab switch (Document PiP for Chrome/Edge)
  const {
    pipWindow,
    isDocumentPipSupported: hasDocumentPip,
    isVideoPipSupported: hasVideoPip,
    closePipWindow
  } = usePictureInPicture({
    isActive: !props.isLeaving && !isWaiting && !props.isScreenSharing
  });

  // Video element PiP fallback for Firefox/Safari
  const { registerVideoElement, closePip: closeVideoPip } = useVideoPictureInPicture({
    isActive: !props.isLeaving && !hasDocumentPip && hasVideoPip && !isWaiting && !props.isScreenSharing
  });

  const handleClosePip = useCallback(async () => {
    if (hasDocumentPip) {
      closePipWindow();
    } else if (hasVideoPip) {
      await closeVideoPip();
    }
  }, [hasDocumentPip, hasVideoPip, closePipWindow, closeVideoPip]);

  // Handle leave meeting from PiP window
  const handleLeaveMeetingFromPip = useCallback(() => {
    // Close PiP first, then focus main window
    handleClosePip();
    focusMeetingTab();
    // Trigger leave modal
    props.onLeave();
  }, [handleClosePip, focusMeetingTab, props]);

  // Calculate total approved participants count (exclude waiting room)
  const totalApprovedParticipantsCount = useMemo(() => {
    return participants.filter((p) => {
      // Include local participant
      if (p.isLocal) return true;
      // Exclude waiting room participants
      if (p.permissions && p.permissions.canPublish === false) return false;
      return true;
    }).length;
  }, [participants]);

  // Render PiP Portal if window is open (Document PiP only)
  const pipPortal = pipWindow ? (
    <PipPortal pipWindow={pipWindow}>
      <PipContent
        tracks={pipTracks}
        totalParticipantCount={totalApprovedParticipantsCount}
        participantAvatarByIdentity={participantAvatarByIdentity}
        localAvatarUrl={localAvatarUrl}
        isMicEnabled={props.isMicEnabled}
        isCameraEnabled={props.isCameraEnabled}
        isScreenShareEnabled={props.isScreenSharing}
        onToggleMic={props.onToggleMic}
        onToggleCamera={props.onToggleCamera}
        onToggleScreenShare={props.onToggleScreenShare}
        onExpandParticipants={() => {
          focusMeetingTab();
          if (!props.showParticipants) {
            props.onToggleParticipants();
          }
        }}
        onLeaveMeeting={handleLeaveMeetingFromPip}
      />
    </PipPortal>
  ) : null;

  // State for Zoom level
  const [zoomLevel, setZoomLevel] = useState(1);

  // Refs for Drag/Pan logic
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const scrollLeft = useRef(0);
  const scrollTop = useRef(0);

  // Banner state
  const isBannerVisible = props.isScreenSharing || isSomeoneElseSharing;
  const bannerPresenterName = props.isScreenSharing
    ? localParticipantName
    : remoteScreenSharer?.name || remoteScreenSharer?.identity || "Unknown";

  // Optimistic UI: If leaving, show the "You left" screen immediately
  if (props.isLeaving) {
    const isEndedForAll = props.loadingOption === HostLeaveOption.END_FOR_ALL;
    const canReopen =
      props.connectionDetails.isHost || props.connectionDetails.canModerate || props.isHost || props.isModerator;
    return <MeetingEndedView roomName={props.connectionDetails.roomName} ended={isEndedForAll} canReopen={canReopen} />;
  }

  const handleFullScreen = () => {
    if (scrollContainerRef.current) {
      if (!document.fullscreenElement) {
        scrollContainerRef.current.requestFullscreen().catch((err) => {
          Logger.error("MeetContent", "Error attempting to enable fullscreen:", err);
        });
      } else {
        document.exitFullscreen();
      }
    }
  };

  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + 0.25, 2)); // Max 2x (200%) zoom
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - 0.25, 0.5)); // Min 0.5x (50%) zoom
  };

  // Drag Handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoomLevel <= 1 || !scrollContainerRef.current) return;
    isDragging.current = true;
    startX.current = e.pageX - scrollContainerRef.current.offsetLeft;
    startY.current = e.pageY - scrollContainerRef.current.offsetTop;
    scrollLeft.current = scrollContainerRef.current.scrollLeft;
    scrollTop.current = scrollContainerRef.current.scrollTop;
    e.preventDefault(); // Prevent text selection
  };

  const handleMouseLeave = () => {
    isDragging.current = false;
  };

  const handleMouseUp = () => {
    isDragging.current = false;
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current || !scrollContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const y = e.pageY - scrollContainerRef.current.offsetTop;
    const walkX = (x - startX.current) * 1; // Scroll speed multiplier
    const walkY = (y - startY.current) * 1;
    scrollContainerRef.current.scrollLeft = scrollLeft.current - walkX;
    scrollContainerRef.current.scrollTop = scrollTop.current - walkY;
  };

  /** Close any open sidebar when clicking on the video area */
  const handleVideoAreaClick = () => {
    if (props.showChat) props.onCloseChat();
    if (props.showParticipants) props.onCloseParticipants();
    if (props.showPolls) props.onClosePolls();
    if (props.showSettings) props.onToggleSettings(); // Toggle closes it if open
    if (props.showNotes) props.onCloseNotes();
    if (props.showLeaveModal) props.onCloseLeaveModal();
  };

  return (
    <LayoutContextProvider value={layoutContext}>
      <div className="flex flex-col h-screen-safe w-screen overflow-hidden bg-[#202124]">
        {isWeb() && (
          <>
            <DebugMode />
            <RecordingIndicator />
            {/* <PipIndicator /> removed per user request */}
            <KeyboardShortcuts
              onToggleChat={props.onToggleChat}
              onToggleParticipants={props.onToggleParticipants}
              onToggleHandRaise={props.onToggleHandRaise}
            />
          </>
        )}

        {/* Reconnecting banner */}
        {props.isReconnecting && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-40 bg-warning/90 backdrop-blur-sm text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 animate-pulse">
            <div className="w-2 h-2 rounded-full bg-white animate-ping" />
            <span className="text-sm font-medium">Reconnecting to the meeting...</span>
          </div>
        )}

        {/* Meeting Details Popup */}
        <MeetingDetails
          isOpen={props.showMeetingDetails}
          onClose={props.onCloseMeetingDetails}
          roomName={props.connectionDetails.roomName}
        />

        {/* Main Content Area - Video + Sidebar side by side */}
        <div className="flex-1 flex overflow-hidden p-2 gap-2">
          {/* Video Area Container - with rounded corners like Google Meet */}
          <div
            ref={videoContainerRef}
            className={`relative flex-1 flex flex-col bg-[#202124] ${isSidebarOpen ? "rounded-xl" : "rounded-lg"}`}
            onClick={handleVideoAreaClick}
          >
            {/* Screen Share Banner */}
            {isBannerVisible && (
              <ScreenShareBanner
                presenterName={bannerPresenterName}
                isLocal={props.isScreenSharing}
                isSharing={isBannerVisible}
                onStopScreenShare={props.onToggleScreenShare}
                onFullScreen={handleFullScreen}
                onZoomIn={handleZoomIn}
                onZoomOut={handleZoomOut}
                zoomLevel={zoomLevel}
              />
            )}

            <div className="flex-1 w-full rounded-xl overflow-y-auto lg:overflow-hidden relative">
              {raisedHandsCount > 0 && (props.isHost || props.isModerator) ? (
                <div
                  className="pointer-events-none absolute top-4 left-1/2 z-50 flex -translate-x-1/2 items-center gap-2 rounded-full bg-amber-400 px-3 py-1.5 text-xs font-semibold text-slate-950 shadow-lg"
                  role="status"
                >
                  <span className="animate-hand-wave text-base leading-none" aria-hidden>
                    ✋
                  </span>
                  <span>{t("participant.handsRaisedBanner", { count: raisedHandsCount })}</span>
                </div>
              ) : null}
              <VideoGrid
                localParticipantSid={localParticipantSid}
                participantReactions={props.participantReactions}
                isSidebarOpen={isSidebarOpen}
                zoomLevel={zoomLevel}
                zoomScrollerRef={scrollContainerRef as React.RefObject<HTMLDivElement>}
                onZoomMouseDown={handleMouseDown}
                onZoomMouseLeave={handleMouseLeave}
                onZoomMouseUp={handleMouseUp}
                onZoomMouseMove={handleMouseMove}
                onToggleSettings={props.onToggleSettings}
                onToggleParticipants={props.onToggleParticipants}
                canModerate={props.isModerator || props.isHost}
                canRequestUnmute={props.isHost}
                onLowerParticipantHand={props.onLowerParticipantHand}
                onMuteParticipant={props.onMuteParticipant}
                onUnmuteParticipant={props.onUnmuteParticipant}
                onMuteParticipantVideo={props.onMuteParticipantVideo}
                onUnmuteParticipantVideo={props.onUnmuteParticipantVideo}
                onKickParticipant={props.onKickParticipant}
                participantAvatarByIdentity={participantAvatarByIdentity}
                localAvatarUrl={localAvatarUrl}
                isLocalScreenSharing={props.isScreenSharing}
                onStopScreenShare={props.onToggleScreenShare}
              />
            </div>
          </div>

          {/* Sidebar Container with smooth animation - Full screen on mobile, sidebar on desktop */}
          <div
            className={`bg-white overflow-hidden shadow-lg transition-[width,opacity,transform] duration-500 ease-[cubic-bezier(0.25,1,0.5,1)] ${
              isSidebarOpen
                ? "fixed inset-x-0 top-0 bottom-18 z-50 rounded-b-xl sm:relative sm:inset-auto sm:z-auto sm:shrink-0 sm:w-96 lg:w-105 sm:rounded-xl opacity-100 sm:translate-x-0 ltr:sm:ml-2 rtl:sm:mr-2"
                : "w-0 opacity-0 translate-x-8 ml-0 pointer-events-none sm:shrink-0 sm:rounded-xl"
            }`}
          >
            {/* Chat Sidebar */}
            <div
              className={`absolute inset-0 h-full w-full flex flex-col transition-[opacity,visibility] duration-300 ${props.showChat && !props.showLeaveModal ? "opacity-100 visible z-10" : "opacity-0 invisible z-0"}`}
            >
              {props.chatIframeEnabled && props.chatIframeId ? (
                <ChatIframeSidebar
                  isOpen={props.showChat}
                  embedUrl={props.chatIframeUrl ?? null}
                  embedKey={props.chatIframeRemountKey ?? null}
                  onClose={props.onCloseChat}
                />
              ) : (
                <ChatSidebar
                  isOpen={props.showChat}
                  onClose={props.onCloseChat}
                  messages={props.chatMessages ?? []}
                  chatInput={props.chatInput ?? ""}
                  onChatInputChange={props.onChatInputChange ?? (() => {})}
                  onSendMessage={props.onSendMessage ?? (() => {})}
                  onFileSelect={props.onFileSelect ?? (() => {})}
                  fileInputRef={props.fileInputRef ?? { current: null }}
                  localParticipantName={localParticipantName}
                />
              )}
            </div>

            {/* Participants Sidebar */}
            <div
              className={`absolute inset-0 h-full w-full flex flex-col transition-[opacity,visibility] duration-300 ${props.showParticipants && !props.showLeaveModal ? "opacity-100 visible z-10" : "opacity-0 invisible z-0"}`}
            >
              <ParticipantsSidebar
                participantsList={props.participantsList}
                isOpen={props.showParticipants}
                isModerator={props.isModerator}
                onClose={props.onCloseParticipants}
                participants={remoteParticipants}
                localParticipantName={localParticipantName}
                localParticipantAudioMuted={!props.isMicEnabled}
                localParticipantVideoMuted={!props.isCameraEnabled}
                hostIdentity={hostIdentity}
                roomName={props.connectionDetails.roomName}
                isRoomLocked={props.isRoomLocked}
                participantReactions={props.participantReactions}
                participantMuteStates={props.participantMuteStates}
                localAvatarUrl={localAvatarUrl}
                participantAvatarByIdentity={participantAvatarByIdentity}
                onToggleRoomLock={props.onToggleRoomLock}
                onMuteParticipant={props.onMuteParticipant}
                onUnmuteParticipant={props.onUnmuteParticipant}
                onMuteParticipantVideo={props.onMuteParticipantVideo}
                onUnmuteParticipantVideo={props.onUnmuteParticipantVideo}
                onKickParticipant={props.onKickParticipant}
                onMuteAllParticipants={props.onMuteAllParticipants}
                onStopAllParticipantVideos={props.onStopAllParticipantVideos}
                onStopAllScreenShares={props.onStopAllScreenShares}
                onLowerParticipantHand={props.onLowerParticipantHand}
              />
            </div>

            {/* Polls Sidebar */}
            <div
              className={`absolute inset-0 h-full w-full flex flex-col transition-[opacity,visibility] duration-300 ${props.showPolls && !props.showLeaveModal ? "opacity-100 visible z-10" : "opacity-0 invisible z-0"}`}
            >
              <PollsSidebar
                isOpen={props.showPolls}
                onClose={props.onClosePolls}
                polls={props.polls}
                onCreatePoll={props.onCreatePoll}
                onVote={props.onVote}
                onClosePoll={props.onClosePoll}
                isHost={props.isHost}
                currentParticipantId={localParticipantSid}
              />
            </div>

            {/* Shared Notes Sidebar */}
            <div
              className={`absolute inset-0 h-full w-full flex flex-col transition-[opacity,visibility] duration-300 ${props.showNotes && !props.showLeaveModal ? "opacity-100 visible z-10" : "opacity-0 invisible z-0"}`}
            >
              <SharedNotes
                isOpen={props.showNotes}
                onClose={props.onCloseNotes}
                notes={props.sharedNotes}
                onUpdateNotes={props.onUpdateNotes}
                onPublishPresence={props.onPublishNotesPresence}
                localIdentity={props.localParticipantIdentity}
                localName={props.localParticipantName}
                remotePresence={props.remoteNotesPresence}
                canEdit={true}
              />
            </div>

            {/* Settings Sidebar */}
            <div
              className={`absolute inset-0 h-full w-full flex flex-col transition-[opacity,visibility] duration-300 ${props.showSettings && !props.showLeaveModal ? "opacity-100 visible z-10" : "opacity-0 invisible z-0"}`}
            >
              <SettingsSidebar
                isOpen={props.showSettings}
                onClose={props.onToggleSettings}
                isRecording={props.isRecording}
                isRecordingLoading={props.isRecordingLoading}
                recordingError={props.recordingError}
                onStartRecording={props.onStartRecording}
                onStopRecording={props.onStopRecording}
                liveCursorsDisabled={props.liveCursorsDisabled}
                onLiveCursorsDisabledChange={props.onLiveCursorsDisabledChange}
                onStopAllParticipantVideos={props.onStopAllParticipantVideos}
                onStopAllScreenShares={props.onStopAllScreenShares}
              />
            </div>

            {/* Leave Sidebar */}
            {props.showLeaveModal && (
              <div className="absolute inset-0 h-full w-full flex flex-col z-50 bg-white">
                <LeaveSidebar
                  isOpen={props.showLeaveModal}
                  onClose={props.onCloseLeaveModal}
                  onLeaveOption={props.onHostLeave}
                  isLoading={props.isLeaving}
                  loadingOption={props.loadingOption}
                  participantCount={props.participantCount}
                  isHost={props.isHost}
                />
              </div>
            )}
          </div>
        </div>

        {/* Whiteboard */}
        <Whiteboard
          isOpen={props.showWhiteboard}
          onClose={props.onCloseWhiteboard}
          onElementsChange={props.onElementsChange}
          onSnapshotChange={props.onWhiteboardSnapshot}
          remoteElements={props.remoteElements}
          isModerator={props.isModerator || props.isHost}
          lockedForParticipants={props.whiteboardLockedForParticipants}
          collaborators={props.whiteboardCollaborators}
          onPointerUpdate={props.onWhiteboardPointerUpdate}
          onToggleLockForParticipants={props.onToggleWhiteboardLock}
        />

        {/* Media Controls - Fixed at bottom, separate from video area */}
        <div className="relative z-50 flex flex-col items-center gap-2">
          {breakoutTheme ? (
            <BreakoutStatusBar
              theme={breakoutTheme}
              showReturn={props.isInBreakout}
              onReturnToMain={props.onReturnToMainMeeting}
            />
          ) : assignedBreakoutPromptTheme ? (
            <BreakoutAssignedPromptBar
              theme={assignedBreakoutPromptTheme}
              onJoinBreakout={() => props.onJoinAssignedBreakout?.()}
            />
          ) : null}
          <MediaControls
            isMicEnabled={props.isMicEnabled}
            isCameraEnabled={props.isCameraEnabled}
            isScreenSharing={props.isScreenSharing}
            isSomeoneElseSharing={remoteParticipants.some((p) => p.isScreenShareEnabled)}
            isHandRaised={props.isHandRaised}
            unreadMessages={props.unreadMessages ?? 0}
            unreadPolls={props.unreadPolls}
            unreadNotes={props.unreadNotes}
            hasUnreadJoinRequests={props.hasUnreadJoinRequests}
            raisedHandsCount={raisedHandsCount}
            onToggleMic={props.onToggleMic}
            onToggleCamera={props.onToggleCamera}
            onToggleScreenShare={props.onToggleScreenShare}
            onToggleChat={props.onToggleChat}
            showChatButton={props.showChatButton ?? true}
            onToggleParticipants={props.onToggleParticipants}
            onTogglePolls={props.onTogglePolls}
            onToggleWhiteboard={props.onToggleWhiteboard}
            onToggleNotes={props.onToggleNotes}
            onToggleSettings={props.onToggleSettings}
            onToggleHandRaise={props.onToggleHandRaise}
            onSendReaction={props.onSendReaction}
            onToggleMeetingDetails={props.onToggleMeetingDetails}
            showBreakoutControl={props.showBreakoutControl}
            isInBreakout={props.isInBreakout}
            onToggleBreakouts={props.onToggleBreakouts}
            onReturnToMainMeeting={props.onReturnToMainMeeting}
            breakoutTimerRemainingSec={props.breakoutTimerRemainingSec}
            breakoutCloseCountdownSec={props.breakoutCloseCountdownSec}
            onLeave={props.onLeave}
          />
        </div>

        {/* Reaction Overlay */}
        <ReactionOverlay reactions={props.reactions} />

        {/* Recording Controls */}
        <RecordingControls
          isRecording={props.isRecording}
          isLoading={props.isRecordingLoading}
          error={props.recordingError}
          recordingStartedAt={props.recordingStartedAt}
          onStartRecording={props.onStartRecording}
          onStopRecording={props.onStopRecording}
          isHost={props.isHost}
        />

        {/* Join requests */}
        {props.isHost && (
          <JoinRequest
            joinRequests={props.joinRequests}
            onApprove={props.onApproveJoinRequest}
            onDeny={props.onDenyJoinRequest}
            onDismiss={props.onDismissJoinRequest}
          />
        )}

        {/* PiP Portal (Document PiP for Chrome/Edge) */}
        {pipPortal}

        {/* Play remote tab/system audio captured alongside a screen share. */}
        <div className="pointer-events-none absolute h-0 w-0 overflow-hidden opacity-0" aria-hidden="true">
          {screenShareAudioTracks.map((track) => (
            <AudioTrack key={track.publication?.trackSid || track.participant.sid} trackRef={track as TrackReference} />
          ))}
        </div>

        {/* Video element for fallback PiP (Firefox/Safari) */}
        {!hasDocumentPip && hasVideoPip && (
          <VideoPipFallback track={primaryPipTrack} onVideoElementReady={registerVideoElement} />
        )}
      </div>
    </LayoutContextProvider>
  );
});

export default MeetContent;
